﻿import { Divider, FormInstance, FormItemProps, Select, Switch } from 'antd';
import InputNumberExt, { Props as InputNumberExtProps } from 'msg-ui-react/dist/components/data-entry/InputNumberExt';
import React from 'react';
import SafetyDepthComponent from '../components/internal/SafetyDepthComponent';
import { ComponentConfigsMap } from '../forms/ComponentsMap';
import { IChartWmsState } from '../models/ChartModels';
import { ChartWmsStore } from '../stores/ChartWmsStore';

const width = 100;
const delay = 500;

const formProps: FormItemProps = {
    labelCol: { flex: `0 1 105px` },
    wrapperCol: { flex: `0 1 ${width}px` }   
}
const precision = 1;

const inputProps: InputNumberExtProps = {
    step: 0.1,
    precision,
    addonAfter: 'm'
}

const hideCustomDepthContours = (state: IChartWmsState) => !state.showCustomDepthContours || !state.useCustomDepthContours;

export const chartWmsConfig: ComponentConfigsMap<IChartWmsState, ChartWmsStore> = {
    useCustomDepthContours: {
        label: "Depth contours",
        hidden: state => !state.showCustomDepthContours,
        formItem: formProps,
        component: <Switch
            style={{ minWidth: 75 }}
            checkedChildren="Custom"
            unCheckedChildren="Default"
        />
    },    
    shallowContour: {
        hidden: hideCustomDepthContours,
        delay,
        formItem: formProps,        
        component: state => <InputNumberExt
            min={0} max={state.deepContour} {...inputProps}
        />
    },
    deepContour: {
        hidden: hideCustomDepthContours,
        delay,
        formItem: formProps,
        component: state => <InputNumberExt
            min={state.shallowContour} {...inputProps}
        />
    },   
    safetyDepth: {
        label: "Safety depth",
        delay,
        hidden: hideCustomDepthContours,
        formItem: {
            ...formProps,
            wrapperCol: { flex: '1 1 0%'},
            dependencies: [
                'shallowContour', 'deepContour',
                'tide', 'shipDraught', 'keelClearance',
                'keelClearanceIsRelative', 'safetyDepthIsCalculated'
            ] as (keyof IChartWmsState)[],
            rules: [    
                {
                    type: 'number',
                    message: "Input must numeric."
                },                      
                form => {
                    const { shallowContour } = (form as FormInstance<IChartWmsState>).getFieldsValue();
                    return {
                        type: 'number',
                        min: shallowContour,
                        message: `Safety depth should be greater than shallow contour.`,
                    }
                },
                form => {
                    const { deepContour } = (form as FormInstance<IChartWmsState>).getFieldsValue();
                    return {
                        type: 'number',
                        max: deepContour,
                        message: `Safety depth should be smaller than deep contour.`
                    }
                }
            ]          
        },  
        component: (state, store, form) => <SafetyDepthComponent
            store={store} state={state} form={form} width={width}
        />       
    },
    tide: {        
        hidden: state => hideCustomDepthContours(state) || !state.safetyDepthIsCalculated,
        delay,
        formItem: formProps,          
        component: <InputNumberExt
            min={0} {...inputProps}
        />
    },
    shipDraught: {        
        hidden: state => hideCustomDepthContours(state) || !state.safetyDepthIsCalculated,
        delay,
        formItem: formProps,
        component: <InputNumberExt
            min={0} {...inputProps}
        />        
    },
    keelClearance: {
        label: "Keel clearance",
        hidden: state => hideCustomDepthContours(state) || !state.safetyDepthIsCalculated,
        delay,
        formItem: formProps,       
        component: (state, store) => <InputNumberExt
            min={0} {...inputProps}
            addonAfter={<Select<"true" | "false">
                value={state.keelClearanceIsRelative ? "true" : "false"}
                onChange={value => store?.setState({ keelClearanceIsRelative: value == 'true' })}
                style={{
                    minWidth: 42
                }}
            >       
                <Select.Option value="false" title="">m</Select.Option>
                <Select.Option value="true" title="">%</Select.Option>                
            </Select>}
        /> 
    },
    useCustomDepthContourInterval: {
        label: "Contour intervals",
        hidden: state => hideCustomDepthContours(state) || !state.showCustomDepthContourInterval,
        formItem: formProps,
        component: <Switch
            style={{ minWidth: 75 }}
            checkedChildren="Custom"
            unCheckedChildren="Default"
        />,
        before: <Divider />        
    },
    depthContourInterval: {
        label: "Contour interval",
        hidden: state => hideCustomDepthContours(state) || !state.showCustomDepthContourInterval || !state.useCustomDepthContourInterval,
        delay,
        formItem: formProps,           
        component: <InputNumberExt {...inputProps} />
    }
};
